import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import ListingCard from '@/components/marketplace/ListingCard';
import { AdSenseUnit } from '@/components/marketplace/AdSenseUnit';
import { Megaphone } from 'lucide-react';

/**
 * SmartSlot — renders one slot inside a card grid.
 *
 * Priority:
 *   1. Active sponsored card matching this placement (+ optional listing_type)
 *   2. Featured listing (paid boost)
 *   3. Urgent listing (paid boost)
 *   4. Top-boosted listing
 *   5. hide slot entirely
 *
 * Sponsored cards are styled like listing cards to blend into the grid
 * with a prominent "SPONSORED" chip.
 */
const SponsoredCard = ({ ad }) => {
  useEffect(() => {
    if (!ad?.id) return;
    api.post(`/sponsored-ads/${ad.id}/impression`).catch(() => {});
  }, [ad?.id]);

  const onClick = () => {
    api.post(`/sponsored-ads/${ad.id}/click`).catch(() => {});
  };

  const isExternal = /^https?:\/\//i.test(ad.click_url);
  const Wrapper = isExternal ? 'a' : Link;
  const wrapperProps = isExternal
    ? { href: ad.click_url, target: '_blank', rel: 'noreferrer sponsored', onClick }
    : { to: ad.click_url || '#', onClick };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`sponsored-card-${ad.placement}`}
      className='group block'
    >
      <Card className='overflow-hidden hover:shadow-md hover:shadow-black/10 transition-shadow h-full flex flex-col ring-1 ring-primary/10'>
        <div className='relative aspect-[4/3] bg-muted overflow-hidden'>
          {ad.image_url ? (
            <img
              src={ad.image_url}
              alt={ad.title}
              className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-500'
              loading='lazy'
            />
          ) : (
            <div className='w-full h-full flex items-center justify-center text-muted-foreground bg-secondary'>
              <Megaphone className='h-8 w-8' />
            </div>
          )}
          <span className='absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full font-semibold uppercase bg-primary text-primary-foreground'>
            Sponsored
          </span>
        </div>
        <div className='p-4 flex-1 flex flex-col gap-2'>
          <h3 className='font-semibold leading-snug line-clamp-2 group-hover:underline underline-offset-4'>
            {ad.title}
          </h3>
          {ad.subtitle && (
            <p className='text-sm text-muted-foreground line-clamp-2'>{ad.subtitle}</p>
          )}
          {ad.advertiser_name && (
            <div className='mt-auto text-[11px] text-muted-foreground'>Sponsored by {ad.advertiser_name}</div>
          )}
        </div>
      </Card>
    </Wrapper>
  );
};

export const SmartSlot = ({ placement = 'listing_grid', listingType = null, excludeIds = [] }) => {
  const [item, setItem] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [adStatus, setAdStatus] = useState(null); // null=pending, 'filled'|'unfilled'|'disabled'

  useEffect(() => {
    const params = { placement, limit: 1 };
    if (listingType) params.listing_type = listingType;
    if (excludeIds && excludeIds.length) params.exclude_ids = excludeIds.join(',');
    api.get('/sponsored-ads/slot', { params })
      .then(r => setItem((r.data || [])[0] || null))
      .catch(() => setItem(null))
      .finally(() => setLoaded(true));
    // eslint-disable-next-line
  }, [placement, listingType, JSON.stringify(excludeIds || [])]);

  if (!loaded) return null;
  // House ads / boosted listings take priority; when the slot has neither,
  // backfill with AdSense. If AdSense is disabled or unfilled, the slot
  // collapses entirely (no empty grid cell / blank space).
  if (!item) {
    if (adStatus === 'unfilled' || adStatus === 'disabled') return null;
    return <AdSenseUnit onResolved={setAdStatus} />;
  }

  if (item.kind === 'ad') return <SponsoredCard ad={item.data} />;
  // Fallback boosted listing — render standard ListingCard with a subtle promoted hint
  return (
    <div className='relative' data-testid={`smart-slot-fallback-${item.fallback_boost || 'boost'}`}>
      <ListingCard listing={item.data} />
    </div>
  );
};

export default SmartSlot;
